import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import styles from '../Recursion.module.css';
import PalindromeEngine from '../recursionengine/PalindromeEngine';
import { RecursionCommands } from '../../../types/RecursionCommands';

export const PALINDROME_CODE = `
function isPalindrome(s, left, right):
    if left >= right:
        return true
    if s[left] != s[right]:
        return false
    return isPalindrome(s, left + 1, right - 1)
`;

interface PalindromeWrapperProps {
  command?: RecursionCommands | null;
  onComplete?: () => void;
}

const PalindromeWrapper: React.FC<PalindromeWrapperProps> = ({ 
  command: externalCommand, 
  onComplete 
}) => {
  const navigate = useNavigate();
  const [internalCommand, setInternalCommand] = useState<RecursionCommands | null>(null);
  const [inputValue, setInputValue] = useState('racecar');
  const [activeLine, setActiveLine] = useState<number | null>(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [finalResult, setFinalResult] = useState<boolean | null>(null);

  useEffect(() => {
    if (externalCommand) {
      setInternalCommand(externalCommand);
      
      if (externalCommand.action === 'generate') {
        const pals = ['racecar', 'level', 'noon', 'civic', 'kayak', 'rotor', 'madam'];
        const non = ['hello', 'world', 'coding', 'react', 'javascript'];
        const all = [...pals, ...non];
        const pick = all[Math.floor(Math.random() * all.length)];
        setInputValue(pick);
      }
      
      if (externalCommand.action === 'reset') {
        setFinalResult(null);
      }
    }
  }, [externalCommand]);

  const handleRun = () => {
    if (isAnimating) return;
    setInternalCommand({ id: Date.now(), action: 'run' });
  };

  const handleStop = () => {
    if (!isAnimating) return;
    setInternalCommand({ id: Date.now(), action: 'stop' });
  };

  const handleReset = () => {
    setInternalCommand({ id: Date.now(), action: 'reset' });
    setFinalResult(null);
  };

  const handleGenerate = () => {
    if (isAnimating) return;
    const pals = ['racecar', 'level', 'noon', 'civic', 'kayak', 'rotor', 'madam'];
    const non = ['hello', 'world', 'coding', 'react', 'javascript'];
    const all = [...pals, ...non];
    const pick = all[Math.floor(Math.random() * all.length)];
    setInputValue(pick);
  };

  const handleAnimationComplete = () => {
    setIsAnimating(false);
    setActiveLine(null);
    if (onComplete) {
      onComplete();
    }
  };

  return (
    <div className={styles.container}>
      <button onClick={() => navigate(-1)} className={styles.returnButton}>
        &larr; Return
      </button>
      <h1 className={styles.title}>Palindrome Checker</h1>

      <div className={styles.description}>
        <h2>Description</h2>
        <p>A palindrome reads the same backward and forward. This recursive algorithm checks both ends and recursively validates the middle substring.</p>
      </div>

      <div className={styles.useCase}>
        <h2>Use Cases</h2>
        <p>Palindrome checks are used in natural language processing, DNA pattern analysis, and as common recursion interview problems.</p>
      </div>

      <div className={styles.timeComplexity}>
        <h2>Time Complexity</h2>
        <p>Time complexity: <b>O(n)</b>. Space complexity: <b>O(n)</b> due to recursion depth.</p>
      </div>

      <div className={styles.controls}>
        <input
          type="text"
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          disabled={isAnimating}
          placeholder="Enter a word"
          style={{ width: '200px' }}
        />
        <button onClick={handleRun} disabled={isAnimating}>Run</button>
        <button onClick={handleStop} disabled={!isAnimating}>Stop</button>
        <button onClick={handleGenerate} disabled={isAnimating}>Generate</button>
        <button onClick={handleReset}>Reset</button>
      </div>

      <div className={styles.mainContent}>
        <div className={styles.vizAndCode}>
          <PalindromeEngine
            command={internalCommand}
            onComplete={handleAnimationComplete}
            inputValue={inputValue}
            setActiveLine={setActiveLine}
            isAnimating={isAnimating}
            setIsAnimating={setIsAnimating}
            setFinalResult={setFinalResult}
            finalResult={finalResult} 
          />
          <div className={styles.codeDisplay}>
            {PALINDROME_CODE.trim().split('\n').map((line, index) => (
              <div
                key={index}
                className={`${styles.codeLine} ${activeLine === index ? styles.highlightedCode : ''}`}
              >
                {line || ' '}
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};

export default PalindromeWrapper;